/**
 * @file view-business-details.tsx
 * @deprecated Copied to app/modules/procurement/pages/vendors_management/vendor-profile/businessDetails/view-business-details.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/vendors_management/vendor-profile/businessDetails/view-business-details
 * @description Page component for managing view business details in the vendors management section.
 *   Displays a list of view business details with pagination, search functionality, and CRUD operations.
 *   Allows users to view the details of view business details with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.1.10
 */

import { Col, Row } from "react-bootstrap";
import { Profile } from "../../../../../../utils/string";
import {
  CURRENCY,
  PAYMENTMODE,
  PAYMENTTERMS,
  TAXREGTYPES,
} from "../../../../../../utils/staticJSON";
import React from "react";
import Method from "../../../../../../utils/methods";

export const BANKTYPES = [
  { value: 1, label: "Tigo Pesa", title: "Tigo Pesa" },
  { value: 2, label: "M-Pesa", title: "M-Pesa" },
  { value: 3, label: "Airtel Money", title: "Airtel Money" },
  { value: 4, label: "Halo Pesa", title: "Halo Pesa" },
  { value: 5, label: "Azam Pay", title: "Azam Pay" },
  { value: 6, label: "TTCL Pesa", title: "TTCL Pesa" },
];

const ViewBusinessDetails = (props: any) => {
  const business = props.profileData?.business || {};
  const address =
    typeof business.address === "string"
      ? { addressLine1: business.address }
      : business.address || business.addressDetails || {};
  const taxDetails = business.taxDetails || {};

  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const formatWorkingDays = (days: any[]) => {
    if (!days || days.length === 0) return "";

    const numericDays = days
      .map((d: any) => (typeof d === "object" ? d.day : d))
      .map(Number)
      .filter((d: number) => !Number.isNaN(d))
      .sort((a: any, b: any) => a - b);

    const groups: number[][] = [];
    if (numericDays.length > 0) {
      let currentGroup: number[] = [numericDays[0]];

      for (let i = 1; i < numericDays.length; i++) {
        if (numericDays[i] === numericDays[i - 1] + 1) {
          currentGroup.push(numericDays[i]);
        } else {
          groups.push(currentGroup);
          currentGroup = [numericDays[i]];
        }
      }
      groups.push(currentGroup);
    }

    return groups
      .map((group) => {
        if (group.length === 1) {
          return daysOfWeek.find((d) => d.value === group[0])?.label;
        } else {
          const firstDay = daysOfWeek.find((d) => d.value === group[0])?.label;
          const lastDay = daysOfWeek.find(
            (d) => d.value === group[group.length - 1]
          )?.label;
          return `${firstDay}-${lastDay}`;
        }
      })
      .join(", ");
  };

  const findLabel = (
    list: { value: any; label: string }[],
    value: any
  ) => list.find((item) => String(item.value) === String(value))?.label;

  const hasAnnualContract =
    business.hasAnnualContract ||
    business.annualContract ||
    !!business.contractValidFrom ||
    !!business.contractValidTo;

  return (
    <>
      <div className="card-body p-5">
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">{Profile.name}</label>
          </Col>
          <div className="col-lg-10">
            <span className="fw-bolder fs-16 fw-600 text-dark">
              {business.ownerName || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.businessName}
            </label>
          </Col>
          <div className="col-lg-10 fv-row">
            <span className="fs-16 fw-600 text-dark">
              {business.name || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.phoneNumber}
            </label>
          </Col>
          <div className="col-lg-10 fv-row">
            <span className="fs-16 fw-600 text-dark">
              {business.phoneCountry}&nbsp; {business.phone || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">{Profile.email}</label>
          </Col>
          <Col lg={10}>
            <span className="fs-16 fw-600 text-dark">
              {business.email || "-"}
            </span>
          </Col>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Vendor Type:</label>
          </Col>
          <Col lg={10}>
            <span className="fs-16 fw-600 text-dark">
              {Array.isArray(business.types)
                ? business.types
                    .map(
                      (t: any) =>
                        t?.reference?.title ??
                        (typeof t === "string" ? t : "")
                    )
                    .filter(Boolean)
                    .join(", ") || "-"
                : business.types?.reference?.title ?? business.types ?? "-"}
            </span>
          </Col>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">VAT number:</label>
          </Col>
          <Col lg={10}>
            <span className="fs-16 fw-600 text-dark">
              {business.vatNumber || "-"}
            </span>
          </Col>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Legal Name:</label>
          </Col>
          <Col lg={10}>
            <span className="fs-16 fw-600 text-dark">
              {business.legalName || "-"}
            </span>
          </Col>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Trade Name:</label>
          </Col>
          <Col lg={10}>
            <span className="fs-16 fw-600 text-dark">
              {business.tradeName || "-"}
            </span>
          </Col>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.businessAddress}
            </label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {address?.addressLine1 || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">City:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">{address?.city || "-"}</span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">State / Region:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {address?.state || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">PIN / ZIP Code:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {address?.postalCode || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Country:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {address?.countryName || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              Tax Registration Type:
            </label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {taxDetails.type != null && taxDetails.type !== ""
                ? findLabel(TAXREGTYPES, taxDetails.type) || "-"
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Tax ID Number:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {taxDetails.number || business.tinNumber || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Currency:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.currency
                ? findLabel(CURRENCY, business.currency) || business.currency
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Payment Terms:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.paymentTerms != null && business.paymentTerms !== ""
                ? findLabel(PAYMENTTERMS, business.paymentTerms) || "-"
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Credit Limit:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.creditLimit != null && business.creditLimit !== ""
                ? business.creditLimit
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Annual Contract:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {hasAnnualContract ? (
                business.contractValidFrom && business.contractValidTo ? (
                  <>
                    {Method.convertDateToDDMMYYYY(business.contractValidFrom)}{" "}
                    - {Method.convertDateToDDMMYYYY(business.contractValidTo)}
                  </>
                ) : (
                  "Yes"
                )
              ) : (
                "No"
              )}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              Preferred Payment Mode:
            </label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.paymentMode != null && business.paymentMode !== ""
                ? findLabel(PAYMENTMODE, business.paymentMode) || "-"
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Lead Duration:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.leadDuration != null && business.leadDuration !== ""
                ? business.leadDuration
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">Working Days:</label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {Array.isArray(business.workingDays) &&
              business.workingDays.length > 0
                ? formatWorkingDays(business.workingDays) || "-"
                : "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7 align-items-center">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.businessCategory}
            </label>
          </Col>
          <div className="col-lg-10 d-flex align-items-center flex-wrap gap-2">
            {business.categories?.length ? (
              business.categories.map((catval: any, index: number) => (
                <div
                  className="me-3"
                  key={
                    catval.reference?.id ?? catval.reference?.title ?? index
                  }
                >
                  <div className="d-flex flex-center bg-gray-100 p-4 pills-radius">
                    <span className="fw-600 text-black fs-16 px-3">
                      {catval.reference?.title ?? "-"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <>-</>
            )}
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.aboutBusiness}
            </label>
          </Col>
          <div className="col-lg-10">
            <span className="fs-16 fw-600 text-dark">
              {business.about || "-"}
            </span>
          </div>
        </Row>
        <Row className="mb-7">
          <Col lg={2}>
            <label className="fs-16 fw-500 text-dark">
              {Profile.businessType}
            </label>
          </Col>
          <div className="col-lg-10">
            {business.types?.length ? (
              business.types.map((val: any, index: number) => (
                <span className="fs-16 fw-600 text-dark" key={index}>
                  {val?.reference?.title || "-"}
                </span>
              ))
            ) : (
              <>-</>
            )}
          </div>
        </Row>
      </div>
    </>
  );
};

export { ViewBusinessDetails };
